import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, SectionCard, EmptyState, Skeleton } from "@/components/Primitives";
import { StatusChip } from "@/components/StatusChip";
import { fmtDate } from "@/lib/format";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckSquare, Check, CalendarClock, Building2, Flame, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAttention } from "@/context/AttentionContext";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Tasks() {
  const navigate = useNavigate();
  const { refresh: refreshAttention } = useAttention();
  const { user } = useAuth();
  const canDelete = !["sales_rep", "readonly"].includes(user?.role);
  const [sel, setSel] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const toggleTask = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDeleteTasks = async () => {
    try {
      const { data } = await api.post("/tasks/bulk-delete", { ids: [...sel] });
      const failed = data.failed || [];
      if (failed.length) toast.warning(`${data.deleted} deleted. ${failed.length} could not be deleted.`);
      else toast.success(`${data.deleted} task${data.deleted === 1 ? "" : "s"} deleted.`);
      setConfirmDel(false); setSel(new Set()); load(); refreshAttention();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("today");
  const [resch, setResch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/tasks"); setRows(data.items); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const t0 = todayStr();
  const buckets = useMemo(() => {
    const b = { today: [], upcoming: [], overdue: [], completed: [] };
    rows.forEach((t) => {
      const due = (t.due_date || "").slice(0, 10);
      if (t.status === "Completed") b.completed.push(t);
      else if (due && due < t0) b.overdue.push(t);
      else if (due === t0 || !due) b.today.push(t);
      else b.upcoming.push(t);
    });
    return b;
  }, [rows, t0]);

  const complete = async (t) => {
    setRows((p) => p.map((x) => (x.id === t.id ? { ...x, status: "Completed" } : x)));
    try { await api.put(`/tasks/${t.id}`, { status: "Completed" }); toast.success("Task completed"); refreshAttention(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); load(); }
  };
  const reschedule = async (t, date) => {
    if (!date) { setResch(null); return; }
    try { await api.put(`/tasks/${t.id}`, { due_date: date }); toast.success("Task rescheduled"); setResch(null); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const Row = ({ t, overdue }) => (
    <div className="flex items-center gap-3 py-3" data-testid={`task-${t.id}`}>
      {canDelete && <Checkbox checked={sel.has(t.id)} onCheckedChange={() => toggleTask(t.id)} data-testid={`task-select-${t.id}`} />}
      <button onClick={() => complete(t)} disabled={t.status === "Completed"} data-testid={`task-complete-${t.id}`}
        className={cn("h-5 w-5 shrink-0 rounded-md border flex items-center justify-center transition-colors",
          t.status === "Completed" ? "bg-[#9ACC32] border-[#9ACC32] text-white" : "border-slate-300 hover:border-[#00C9E0]")}>
        {t.status === "Completed" && <Check className="h-3.5 w-3.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={cn("text-sm font-medium truncate", t.status === "Completed" ? "text-slate-400 line-through" : "text-slate-800")}>{t.title}</p>
        <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
          {t.business_name && (
            <button onClick={() => t.business_id && navigate(`/businesses/${t.business_id}`)} className="inline-flex items-center gap-1 hover:text-[#0094A8] truncate max-w-[180px]">
              <Building2 className="h-3 w-3" />{t.business_name}
            </button>
          )}
          <span className={cn("inline-flex items-center gap-1 tnum", overdue && "text-[#C42607] font-semibold")}>
            <CalendarClock className="h-3 w-3" />{t.due_date ? fmtDate(t.due_date) : "No date"}
          </span>
        </div>
      </div>
      <StatusChip value={t.priority} />
      {resch === t.id ? (
        <Input type="date" autoFocus className="h-8 w-36" defaultValue={t0}
          onChange={(e) => reschedule(t, e.target.value)} onBlur={() => setResch(null)} data-testid={`task-resch-input-${t.id}`} />
      ) : (
        t.status !== "Completed" && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setResch(t.id)} data-testid={`task-reschedule-${t.id}`}>Reschedule</Button>
      )}
    </div>
  );

  const VIEWS = [
    { key: "today", label: "Today", icon: Flame, count: buckets.today.length },
    { key: "upcoming", label: "Upcoming", icon: CalendarClock, count: buckets.upcoming.length },
    { key: "overdue", label: "Overdue", icon: AlertTriangle, count: buckets.overdue.length },
    { key: "completed", label: "Completed", icon: CheckCircle2, count: buckets.completed.length },
  ];
  const list = buckets[view];

  return (
    <div data-testid="page-tasks">
      <PageHeader title="Tasks & Follow-Ups" subtitle="Your daily sales workflow — stay on top of every follow-up." />

      <div className="rounded-2xl bg-[#0F1724] p-5 mb-6 cc-fade-up relative overflow-hidden" data-testid="todays-focus">
        <div className="absolute -top-10 -right-6 h-40 w-40 rounded-full bg-[#00C9E0]/20 blur-3xl" />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#00C9E0]">Today's Focus</p>
            <p className="mt-1 text-white text-sm">{buckets.overdue.length} overdue · {buckets.today.length} due today · {buckets.completed.length} completed</p>
          </div>
          <div className="flex gap-2">
            {buckets.overdue.length > 0 && <span className="rounded-lg bg-[#FE3967]/20 text-[#FFB1C4] text-xs font-semibold px-3 py-1.5">{buckets.overdue.length} overdue</span>}
            <span className="rounded-lg bg-[#9ACC32]/20 text-[#C5E86C] text-xs font-semibold px-3 py-1.5">{buckets.today.length} today</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} data-testid={`task-view-${v.key}`}
            className={cn("inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
              view === v.key ? "border-[#00C9E0] bg-[#00C9E0]/10 text-[#0094A8]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}>
            <v.icon className="h-3.5 w-3.5" />{v.label}
            <span className={cn("tnum rounded-full px-1.5 text-[11px]", view === v.key ? "bg-[#00C9E0]/20" : "bg-slate-100")}>{v.count}</span>
          </button>
        ))}
      </div>

      {canDelete && sel.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#00C9E0]/30 bg-[#00C9E0]/5 px-4 py-2.5" data-testid="task-bulk-bar">
          <span className="text-sm font-semibold text-slate-700" data-testid="task-bulk-count">{sel.size} selected</span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="border-[#C42607]/40 text-[#C42607] hover:bg-[#C42607]/10" onClick={() => setConfirmDel(true)} data-testid="task-bulk-delete"><Trash2 className="h-4 w-4" />Delete Permanently</Button>
          <Button variant="ghost" size="sm" onClick={() => setSel(new Set())}>Clear Selection</Button>
        </div>
      )}

      <SectionCard>
        {loading ? <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          : list.length === 0 ? <EmptyState icon={CheckSquare}
              title={view === "overdue" ? "No overdue tasks" : view === "completed" ? "No completed tasks yet" : view === "today" ? "Nothing due today" : "Nothing upcoming"}
              hint={view === "today" ? "You're all caught up for today." : "Tasks appear here as they're scheduled."} />
          : <div className="divide-y divide-slate-100">{list.map((t) => <Row key={t.id} t={t} overdue={view === "overdue"} />)}</div>}
      </SectionCard>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {sel.size} task{sel.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>These tasks will be permanently removed from the CRM. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDeleteTasks} className="bg-[#C42607] hover:bg-[#9e1e06]" data-testid="task-confirm-delete">Delete {sel.size} Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
